import React, { useState, useEffect } from 'react'
import {
  Container, Paper, Typography, Box, Button, Alert, CircularProgress,
} from '@mui/material'
import { gql, useQuery, useMutation } from '@apollo/client'
import PayoutConfigForm from '../components/PayoutConfig/PayoutConfigForm'
import EntityPicker from '../components/PayoutConfig/EntityPicker'
import {
  getRestaurantPayoutConfig,
  saveRestaurantPayoutConfig,
} from '../apollo/mutations'
import { restaurantList } from '../apollo/queries'

const GET = gql`${getRestaurantPayoutConfig}`
const SAVE = gql`${saveRestaurantPayoutConfig}`
const RESTAURANTS = gql`${restaurantList}`

const NUM_FIELDS = [
  'opt1HourlyRate', 'opt2HourlyRate', 'opt2PerOrderBonus',
  'opt3FlatFeePerOrder', 'opt4BaseFee', 'opt4PerMileRate',
  'opt5PercentOfDeliveryFee', 'opt6MinimumPerDelivery', 'opt8BonusPerExtraOrder',
]
const BOOL_FIELDS = [
  'opt1Enabled', 'opt2Enabled', 'opt3Enabled', 'opt4Enabled',
  'opt5Enabled', 'opt6Enabled', 'opt8Enabled',
]
function pickInput(cfg) {
  const out = {}
  BOOL_FIELDS.forEach(k => { if (cfg?.[k] != null) out[k] = !!cfg[k] })
  NUM_FIELDS.forEach(k => {
    if (cfg?.[k] != null && cfg[k] !== '') {
      const n = parseFloat(cfg[k])
      if (Number.isFinite(n)) out[k] = n
    }
  })
  return out
}

export default function RestaurantPayoutSettings() {
  // Support deep links: ?restaurantId=N still selects on load.
  const initialId = new URLSearchParams(window.location.search).get('restaurantId') || ''
  const [restaurantId, setRestaurantId] = useState(initialId)

  const { data: restData, loading: restLoading } = useQuery(RESTAURANTS, {
    fetchPolicy: 'cache-and-network',
  })
  const restaurants = restData?.restaurantList || []

  const { data, loading, error, refetch } = useQuery(GET, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })
  const [cfg, setCfg] = useState(null)
  const [saved, setSaved] = useState(false)
  const [save, { loading: saving, error: saveError }] = useMutation(SAVE, {
    onCompleted: () => { setSaved(true); refetch() },
  })

  useEffect(() => {
    if (data?.restaurantPayoutConfig) setCfg(data.restaurantPayoutConfig)
  }, [data])

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Restaurant Staff Driver Payouts (Super-Admin Override)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Cross-restaurant payout configuration for support cases. Restaurants
          can self-manage their own settings from their panel; use this page
          only when you need to fix something on behalf of a restaurant.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          Configures payouts for that restaurant&apos;s in-house staff drivers (driverType: RESTAURANT).
          Restaurant funds these payouts.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <EntityPicker
            options={restaurants}
            value={restaurantId}
            onChange={setRestaurantId}
            loading={restLoading}
            label="Restaurant"
            placeholder="Pick a restaurant to edit"
            getLabel={(o) => o?.name || `Restaurant #${o?._id}`}
            getSubLabel={(o) => o?.address}
          />
        </Box>

        {restaurantId && loading && !data && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError.message}</Alert>}
        {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Saved.</Alert>}

        {restaurantId && cfg && (
          <>
            <PayoutConfigForm value={cfg} onChange={setCfg} disabled={saving} />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => {
                  setSaved(false)
                  save({ variables: { restaurantId, input: pickInput(cfg) } })
                }}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  )
}
